import {
  Button,
  DatePicker,
  FormItem,
  Heading,
  NavigationModal
} from '@lmc/ui-kit'
import { TextArea } from '@lmc/ui-kit/src/dataEntry/Input'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Divider, Form, Modal, TimePicker } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import useClockIn from 'screens/Rostering/hooks/useClockIn'
import type {
  ClockInOutAction,
  ClockInOutType
} from 'screens/Rostering/hooks/useClockInOut'
import useClockOut from 'screens/Rostering/hooks/useClockOut'
import type {
  ClockInTeamMember,
  RotaEventInstance
} from 'screens/Rostering/types'
import { getRequiredRule } from 'screens/Rostering/utils'
import {
  BACKEND_REQUEST_STRING_FORMAT,
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import { styled } from 'styled-components'
import { getPrefilledValues } from './utils'

dayjs.extend(isoWeek)

type ClockInOutModalProps = {
  open: boolean
  onClose: () => void
  revisionDetail: RotaEventInstance
  type: ClockInOutType
  action: ClockInOutAction
  teamMember?: ClockInTeamMember
}

export const ClockInOutModal = ({
  open,
  onClose,
  revisionDetail,
  type,
  action,
  teamMember
}: ClockInOutModalProps) => {
  const [form] = AntdForm.useForm()
  const sendEvent = useMixpanelEvent()
  const location = useLocation()

  const teamMemberId = teamMember?.teamMemberId

  const { clockIn } = useClockIn(revisionDetail.id)
  const { clockOut } = useClockOut(revisionDetail.id)

  const clockInOutDetails = useMemo(
    () =>
      revisionDetail?.clockIns?.find(
        (clockIn) => clockIn.teamMemberId === teamMemberId
      ),
    [revisionDetail, teamMemberId]
  )

  const initialValues = getPrefilledValues({
    clockInOutDetails,
    type,
    action,
    timezone: revisionDetail?.timezone,
    revisionDetail
  })

  const [formData, setFormData] =
    useState<Record<string, unknown>>(initialValues)

  const [isDirty, setIsDirty] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)

  const { t } = useTranslation()

  const handleClose = () => {
    if (isDirty) {
      setShowPrompt(true)
      return
    }

    onClose()
  }

  const handleSave = async () => {
    try {
      await form.validateFields()
    } catch {
      // validation errors are rendered against the fields themselves
      return
    }

    const values = form.getFieldsValue()

    if (action === 'edit') {
      const originalTime =
        type === 'in'
          ? clockInOutDetails?.clockInTime ||
            clockInOutDetails?.clockInTimeSystem
          : clockInOutDetails?.clockOutTime ||
            clockInOutDetails?.clockOutTimeSystem

      const newDateTime = dayjs(values.date)
        .set('hour', dayjs(values.time).hour())
        .set('minute', dayjs(values.time).minute())

      sendEvent(MixpanelEvents.RECONCILIATION_CLOCK_IN_OUT_EDITED, {
        clockType: type,
        originalTime: originalTime
          ? dayjs(originalTime).toISOString()
          : undefined,
        newTime: newDateTime.toISOString(),
        source: location.pathname.includes('reconciliation')
          ? 'reconciliation'
          : 'builder'
      })
    }

    setIsDirty(false)

    const date = dayjs(values.date)
      .set('hour', dayjs(values.time).hour())
      .set('minute', dayjs(values.time).minute())

    const instanceTime = date.tz(revisionDetail?.timezone)
    const offsetMinutes = instanceTime.utcOffset()
    const adjusted = date.subtract(offsetMinutes, 'minute')

    const args = {
      action,
      note: values.note,
      dateTime: adjusted.format(BACKEND_REQUEST_STRING_FORMAT),
      teamMemberId
      // lat: 51.530546,
      // lng: -0.134293,
      // accuracy: 10,
    }

    if (type === 'in') {
      clockIn(args)
    } else {
      clockOut(args)
    }

    onClose()
  }

  const handleFormValuesChange = (values: Record<string, unknown>) => {
    if (values) {
      setIsDirty(true)
    }

    setFormData((prev) => ({ ...prev, ...values }))
  }

  return (
    <Modal
      width={580}
      open={open}
      onCancel={handleClose}
      destroyOnClose
      footer={
        <FooterWrapper>
          <FooterRightSide>
            <Button onClick={handleClose}>{t('CANCEL')}</Button>
            <Button variant="primary" onClick={handleSave}>
              {action === 'edit' ? t('SAVE') : t(`CLOCK_${type.toUpperCase()}`)}
            </Button>
          </FooterRightSide>
        </FooterWrapper>
      }
    >
      <Container>
        <StyledHeading level={3}>
          {t(
            `${action.toUpperCase()}_CLOCK_${type.toUpperCase()}_FOR_{{name}}`,
            {
              name: `${teamMember?.carer?.name?.first} ${teamMember?.carer?.name?.last}`
            }
          )}
        </StyledHeading>

        <StyledDivider />

        <InfoSectionWrapper>
          <InfoSection>
            <StyledText level={2}>{t('PLANNED_CLOCK_IN')}</StyledText>
            <Text weight="bold">
              {getDateTimeInZone(revisionDetail?.startTime).format(TIME_FORMAT)}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledText level={2}>{t('PLANNED_CLOCK_OUT')}</StyledText>
            <Text weight="bold">
              {getDateTimeInZone(revisionDetail?.endTime).format(TIME_FORMAT)}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledText level={2}>{t('PLANNED_DURATION')}</StyledText>
            <Text weight="bold">{revisionDetail?.expectedDuration}</Text>
          </InfoSection>
        </InfoSectionWrapper>

        <StyledForm
          form={form}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
          layout="vertical"
        >
          <FormItem
            name="time"
            label={<Text>{t(`CLOCK_${type.toUpperCase()}_TIME`)}</Text>}
            rules={[getRequiredRule(t(`CLOCK_${type.toUpperCase()}_TIME`))]}
          >
            <StyledTimePicker
              format="HH:mm"
              changeOnScroll
              needConfirm={false}
              order={false}
            />
          </FormItem>
          <FormItem
            name="date"
            label={<Text>{t(`CLOCK_${type.toUpperCase()}_DATE`)}</Text>}
            rules={[getRequiredRule(t(`CLOCK_${type.toUpperCase()}_DATE`))]}
          >
            <DatePicker />
          </FormItem>
          <FormItem name="note" label={<Text>{t('NOTES')}</Text>}>
            <TextArea
              autoSize={{
                minRows: 3
              }}
            />
          </FormItem>

          <Text>{t('DETAILS_PUBLISHED')}</Text>
        </StyledForm>
        <StyledDivider />
      </Container>
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('EXIT_EVENT')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={() => {
          setShowPrompt(false)
          setIsDirty(false)
          onClose()
        }}
        cancelNavigation={() => setShowPrompt(false)}
      />
    </Modal>
  )
}

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const StyledDivider = styled(Divider)`
  margin: 8px 0;
`

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 24px;
`

const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
` as unknown as typeof Form

const StyledTimePicker = styled(TimePicker)`
  width: 40%;
`
const InfoSectionWrapper = styled.div`
  display: flex;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
  align-items: center;
  justify-content: space-between;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 150px;
`
const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
